import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider } from "@/context/AuthContext";
import PhoneShell from "@/components/PhoneShell";
import ProtectedRoute from "@/components/ProtectedRoute";

import Splash from "@/screens/Splash";
import Welcome from "@/screens/auth/Welcome";
import Login from "@/screens/auth/Login";
import SignupMethod from "@/screens/auth/signup/Method";
import SignupIdentifier from "@/screens/auth/signup/Identifier";
import SignupDetails from "@/screens/auth/signup/Details";
import SignupContact from "@/screens/auth/signup/Contact";
import SignupAvatar from "@/screens/auth/signup/Avatar";
import OTPScreen from "@/screens/auth/OTPScreen";
import ForgotIdentifier from "@/screens/auth/forgot/Identifier";
import ForgotReset from "@/screens/auth/forgot/Reset";

import OnboardingIntents from "@/screens/onboarding/Intents";
import OnboardingInterests from "@/screens/onboarding/Interests";

import TabLayout from "@/components/TabLayout";
import Home from "@/screens/home/Home";
import Search from "@/screens/home/Search";
import NearMe from "@/screens/home/NearMe";
import DiscoverList from "@/screens/home/DiscoverList";

import Inbox from "@/screens/messages/Inbox";
import Chat from "@/screens/messages/Chat";
import Notifications from "@/screens/notifications/Notifications";
import Discover from "@/screens/discover/Discover";
import FollowList from "@/screens/profile/FollowList";
import UpgradeBusiness from "@/screens/business/UpgradeBusiness";
import VerifyDocs from "@/screens/verification/VerifyDocs";
import MessageSettings from "@/screens/settings/MessageSettings";
import Profile from "@/screens/profile/Profile";
import EditProfile from "@/screens/profile/EditProfile";
import Manage from "@/screens/profile/Manage";
import PublicProfile from "@/screens/profile/PublicProfile";
import CreateService from "@/screens/services/CreateService";
import ServiceDetail from "@/screens/services/ServiceDetail";
import CreateEvent from "@/screens/events/CreateEvent";
import EventDetail from "@/screens/events/EventDetail";
import EventChat from "@/screens/events/EventChat";

import Insights from "@/screens/insights/Insights";
import Premium from "@/screens/premium/Premium";
import UpgradeSuccess from "@/screens/premium/UpgradeSuccess";
import Verification from "@/screens/verification/Verification";
import Boost from "@/screens/boost/Boost";
import Wallet from "@/screens/wallet/Wallet";
import PayoutMethods from "@/screens/wallet/PayoutMethods";
import Withdraw from "@/screens/wallet/Withdraw";
import TransactionsScreen from "@/screens/wallet/Transactions";
import Bookings from "@/screens/bookings/Bookings";
import BookingDetail from "@/screens/bookings/BookingDetail";
import Referral from "@/screens/referral/Referral";
import Settings from "@/screens/settings/Settings";
import { HelpCenter, ReportProblem, About } from "@/screens/settings/Info";
import SafetyCenter from "@/screens/safety/SafetyCenter";
import RefundPolicy from "@/screens/legal/RefundPolicy";

import AdminRoute from "@/components/AdminRoute";
import AdminLogin from "@/screens/admin/AdminLogin";
import AdminLayout from "@/screens/admin/AdminLayout";
import AdminDashboard from "@/screens/admin/AdminDashboard";
import AdminUsers from "@/screens/admin/AdminUsers";
import AdminHealth from "@/screens/admin/AdminHealth";
import AdminSupport from "@/screens/admin/AdminSupport";
import Support, { SupportTicket } from "@/screens/support/Support";
import AccountStatusGuard from "@/components/AccountStatusGuard";
import {
    AdminListings,
    AdminVerifications,
    AdminBookings,
    AdminTransactions,
    AdminWithdrawals,
    AdminDisputes,
    AdminReports,
    AdminSettings,
} from "@/screens/admin/AdminPages";

const P = ({ children }) => <ProtectedRoute><AccountStatusGuard>{children}</AccountStatusGuard></ProtectedRoute>;
const A = ({ children }) => <AdminRoute>{children}</AdminRoute>;

function App() {
    return (
        <ThemeProvider>
            <AuthProvider>
                <BrowserRouter>
                    <PhoneShell>
                        <Routes>
                            <Route path="/" element={<Splash />} />

                            {/* Auth */}
                            <Route path="/auth" element={<Welcome />} />
                            <Route path="/auth/login" element={<Login />} />
                            <Route path="/auth/signup" element={<SignupMethod />} />
                            <Route path="/auth/signup/identifier" element={<SignupIdentifier />} />
                            <Route path="/auth/signup/otp" element={<OTPScreen purpose="signup" nextRoute="/auth/signup/details" testIdPrefix="signup-otp" />} />
                            <Route path="/auth/signup/details" element={<SignupDetails />} />
                            <Route path="/auth/signup/contact" element={<SignupContact />} />
                            <Route path="/auth/signup/avatar" element={<SignupAvatar />} />
                            <Route path="/auth/forgot" element={<ForgotIdentifier />} />
                            <Route path="/auth/forgot/otp" element={<OTPScreen purpose="forgot" nextRoute="/auth/forgot/reset" testIdPrefix="forgot-otp" />} />
                            <Route path="/auth/forgot/reset" element={<ForgotReset />} />

                            {/* Onboarding */}
                            <Route path="/onboarding" element={<P><OnboardingIntents /></P>} />
                            <Route path="/onboarding/interests" element={<P><OnboardingInterests /></P>} />

                            {/* Standalone protected */}
                            <Route path="/search" element={<P><Search /></P>} />
                            <Route path="/near-me" element={<P><NearMe /></P>} />
                            <Route path="/discover/:kind" element={<P><DiscoverList /></P>} />
                            <Route path="/messages/:convId" element={<P><Chat /></P>} />
                            <Route path="/services/:id" element={<P><ServiceDetail /></P>} />
                            <Route path="/events/:id" element={<P><EventDetail /></P>} />
                            <Route path="/events/:id/chat" element={<P><EventChat /></P>} />
                            <Route path="/create/service" element={<P><CreateService /></P>} />
                            <Route path="/create/event" element={<P><CreateEvent /></P>} />
                            <Route path="/edit/service/:id" element={<P><CreateService /></P>} />
                            <Route path="/edit/event/:id" element={<P><CreateEvent /></P>} />

                            {/* Followers / Following — own + public */}
                            <Route path="/profile/followers" element={<P><FollowList kind="followers" /></P>} />
                            <Route path="/profile/following" element={<P><FollowList kind="following" /></P>} />
                            <Route path="/u/:username/followers" element={<P><FollowList kind="followers" /></P>} />
                            <Route path="/u/:username/following" element={<P><FollowList kind="following" /></P>} />

                            {/* Business upgrade + document verification */}
                            <Route path="/profile/upgrade-business" element={<P><UpgradeBusiness /></P>} />
                            <Route path="/verification/:kind" element={<P><VerifyDocs /></P>} />
                            <Route path="/settings/messaging" element={<P><MessageSettings /></P>} />
                            <Route path="/profile/edit" element={<P><EditProfile /></P>} />
                            <Route path="/manage" element={<P><Manage /></P>} />
                            <Route path="/u/:username" element={<P><PublicProfile /></P>} />

                            {/* Monetization + trust */}
                            <Route path="/insights" element={<P><Insights /></P>} />
                            <Route path="/premium" element={<P><Premium /></P>} />
                            <Route path="/premium/success" element={<P><UpgradeSuccess /></P>} />
                            <Route path="/verification" element={<P><Verification /></P>} />
                            <Route path="/boost" element={<P><Boost /></P>} />

                            {/* Wallet */}
                            <Route path="/wallet" element={<P><Wallet /></P>} />
                            <Route path="/wallet/payouts" element={<P><PayoutMethods /></P>} />
                            <Route path="/wallet/withdraw" element={<P><Withdraw /></P>} />
                            <Route path="/wallet/transactions" element={<P><TransactionsScreen /></P>} />

                            {/* Bookings */}
                            <Route path="/bookings" element={<P><Bookings /></P>} />
                            <Route path="/bookings/:id" element={<P><BookingDetail /></P>} />

                            {/* Referral */}
                            <Route path="/referral" element={<P><Referral /></P>} />

                            {/* Settings + Safety + Info */}
                            <Route path="/settings" element={<P><Settings /></P>} />
                            <Route path="/safety" element={<P><SafetyCenter /></P>} />
                            <Route path="/help" element={<P><HelpCenter /></P>} />
                            <Route path="/support" element={<ProtectedRoute><AccountStatusGuard><Support /></AccountStatusGuard></ProtectedRoute>} />
                            <Route path="/support/:id" element={<ProtectedRoute><AccountStatusGuard><SupportTicket /></AccountStatusGuard></ProtectedRoute>} />
                            <Route path="/report" element={<P><ReportProblem /></P>} />
                            <Route path="/about" element={<P><About /></P>} />
                            <Route path="/refund-policy" element={<RefundPolicy />} />

                            {/* Protected app tabs */}
                            <Route element={<P><TabLayout /></P>}>
                                <Route path="/home" element={<Home />} />
                                <Route path="/messages" element={<Inbox />} />
                                <Route path="/discover" element={<Discover />} />
                                <Route path="/notifications" element={<Notifications />} />
                                <Route path="/profile" element={<Profile />} />
                            </Route>

                            {/* Admin panel — full width, bypasses PhoneShell */}
                            <Route path="/admin/login" element={<AdminLogin />} />
                            <Route path="/admin" element={<A><AdminLayout /></A>}>
                                <Route index element={<AdminDashboard />} />
                                <Route path="users" element={<AdminUsers />} />
                                <Route path="listings" element={<AdminListings />} />
                                <Route path="verifications" element={<AdminVerifications />} />
                                <Route path="bookings" element={<AdminBookings />} />
                                <Route path="transactions" element={<AdminTransactions />} />
                                <Route path="withdrawals" element={<AdminWithdrawals />} />
                                <Route path="disputes" element={<AdminDisputes />} />
                                <Route path="reports" element={<AdminReports />} />
                                <Route path="support" element={<AdminSupport />} />
                                <Route path="health" element={<AdminHealth />} />
                                <Route path="settings" element={<AdminSettings />} />
                            </Route>

                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                    </PhoneShell>
                </BrowserRouter>
            </AuthProvider>
        </ThemeProvider>
    );
}

export default App;
